import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import api from '@/lib/api';
import { PageSEO } from '@/components/SEO';
import { getImageUrl } from '@/lib/utils';

export default function Collections() {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    try {
      const res = await api.get('/site-settings');
      setCollections(res.data?.collections || []);
    } catch (error) {
      console.error('Failed to load collections:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-[3/4] skeleton rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="collections-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium mb-4">
            Our Collections
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            Discover our curated collections of premium shawls, each designed to complement your unique style
          </p>
        </div>

        {/* Collections Grid */}
        {collections.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {collections.map((collection, index) => (
              <Link
                key={index}
                to={`/shop?collection=${encodeURIComponent(collection.name)}`}
                className="group relative block overflow-hidden rounded-lg bg-stone-100"
                data-testid={`collection-card-${index}`}
              >
                {/* Image */}
                <div className="aspect-[3/4] overflow-hidden">
                  {collection.image ? (
                    <img
                      src={getImageUrl(collection.image)}
                      alt={collection.name}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="h-full w-full bg-gradient-to-br from-stone-200 to-stone-300 flex items-center justify-center">
                      <span className="text-stone-400 text-lg">No Image</span>
                    </div>
                  )}
                </div>

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-6">
                  <h3 className="text-xl md:text-2xl font-medium text-white mb-2">
                    {collection.name}
                  </h3>
                  {collection.description && (
                    <p className="text-white/80 text-sm mb-4 line-clamp-2">
                      {collection.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 text-white text-sm font-medium opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                    <span>Shop Collection</span>
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-stone-500 text-lg">No collections available</p>
            <Link 
              to="/shop" 
              className="inline-block mt-4 text-stone-900 underline hover:no-underline"
            >
              Browse all products
            </Link>
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-20 text-center">
          <p className="text-stone-600 mb-4">Looking for something specific?</p>
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 bg-stone-900 text-white px-8 py-3 rounded-full hover:bg-stone-800 transition-colors"
          >
            Browse All Products
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
